import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box, Divider, Stack, Typography } from "@mui/material"
import { NumericInput } from "components/controls/numbericInput"
import { Section } from "components/controls/section"
import { ValueSpan } from "components/controls/valueSpan"
import { FormDialog } from "components/dialog/form"
import { numberFormat } from "shared/util"

type SetExtendCountDialogProps = {
	listTitle: string
	sourceCount: number
	extendCount: number
	limitted: number | null
	rateThreshold: number
	onApply: (rateThreshold: number, limitted: number) => void
	onClose: () => void
}
export const SetExtendCountDialog = ({
	listTitle,
	sourceCount,
	extendCount: extendCountOrigin,
	limitted: limittedOrigin,
	rateThreshold: rateThresholdOrigin,
	onApply,
	onClose
}: SetExtendCountDialogProps) => {
	const firstFocusInputRef = useRef<HTMLInputElement>(null)
	const [extendCount, setExtendCount] = useState<number>(
		limittedOrigin ? limittedOrigin : Math.floor((extendCountOrigin * rateThresholdOrigin) / 100)
	)
	const { totalCount, rateThreshold } = useMemo(
		() => {
			if (extendCountOrigin > extendCount) {
				return {
					totalCount: sourceCount + extendCount,
					rateThreshold: Math.ceil((extendCount / extendCountOrigin) * 100)
				}
			} else {
				return {
					totalCount: sourceCount + extendCountOrigin,
					rateThreshold: 100
				}
			}
		},
		[sourceCount, extendCountOrigin, extendCount]
	)
	const onSubmit = useCallback(
		() => {
			onApply(rateThreshold, extendCount)
			onClose()
		},
		[onApply, onClose, extendCount, rateThreshold]
	)
	useEffect(
		() => {
			const time = setTimeout(() => firstFocusInputRef.current?.focus(), 0)
			return () => clearTimeout(time)
		},
		[firstFocusInputRef]
	)
	return (
		<FormDialog
			open
			fullWidth
			autoFocus
			maxWidth="sm"
			title={`${listTitle}拡張人数指定`}
			formGroupProps={{ sx: { alignItems: "center" }}}
			body={
				<Stack direction="column">
					<Section label="元ターゲット数" labelVariant="body1">
						<ValueSpan text={numberFormat(sourceCount, '-')} suffix="人"/>
					</Section>
					<Section label="＋ 拡張人数" labelVariant="body1" labelProps={{ paddingY: 2 }}>
						<Stack direction="row" spacing={1} alignItems="center">
							<Stack direction="row" spacing={1}>
								<ValueSpan text={numberFormat(extendCountOrigin)} suffix="人 ×"/>
								<ValueSpan text={`${rateThreshold}`} suffix="% ="/>
							</Stack>
							<Box width="8em">
								<NumericInput
									inputRef={firstFocusInputRef}
									variant="outlined"
									size="small"
									autoFocus
									state={`${extendCount}`}
									setState={(value) => setExtendCount(Number(value))}
								/>
							</Box>
							<Typography variant="body2" color='text.secondary'>人</Typography>
						</Stack>
					</Section>
					<Divider/>
					<Section label="＝ 合計人数" labelVariant="body1">
						<ValueSpan text={numberFormat(totalCount)} suffix="人"/>
					</Section>
				</Stack>
			}
			submitLabel="適用"
      onSubmit={onSubmit}
			onClose={onClose}
		/>
	)
}